import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Handyman as HandymanIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ConfirmDialog from '../common/ConfirmDialog';
import FixFailedImportAppointmentsDialog from './FixFailedImportAppointmentsDialog';
import {
  buildFailingAppointmentsCsv,
  buildImportAppointmentsPayload,
  groupAppointmentRowsByReference,
  parseFreshaAppointmentCsv,
  resolveAppointmentRowsWithCatalogs,
} from '../../utils/freshaAppointmentCsv';
import StoreDataImportService from '../../services/storeDataImportService';
import StoreCustomerService from '../../services/storeCustomerService';
import TeamMemberService from '../../services/teamMemberService';
import StoreService from '../../services/storeService';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const FRESHA_APPOINTMENTS_EXPORT_URL = 'https://partners.fresha.com/calendar';

const uploadButtonSx = {
  borderColor: BRAND.accent,
  color: BRAND.accentText,
  textTransform: 'none',
  fontWeight: 600,
  height: 40,
  px: 2,
  '&:hover': { borderColor: BRAND.accentDark, backgroundColor: brandAlpha('accent', 0.08) },
  '&.Mui-disabled': {
    borderColor: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
  },
};

const textActionSx = {
  color: BRAND.accentText,
  textTransform: 'none',
  fontWeight: 600,
  px: 0.5,
  minWidth: 0,
  '&:hover': {
    backgroundColor: 'transparent',
    color: BRAND.accentDark,
    textDecoration: 'underline',
  },
  '&.Mui-disabled': {
    color: 'rgba(0, 0, 0, 0.26)',
  },
};

const downloadTextFile = (content, fileName, mimeType = 'text/csv;charset=utf-8') => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ImportAppointmentsStep = ({
  storeId,
  onCompleted,
  disabled = false,
}) => {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [delimiter, setDelimiter] = useState(',');
  const [fileName, setFileName] = useState('');
  const [parseErrorKey, setParseErrorKey] = useState(null);
  const [fixOpen, setFixOpen] = useState(false);
  const [importReadyConfirmOpen, setImportReadyConfirmOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [catalogs, setCatalogs] = useState(null);

  const activeRows = useMemo(() => rows.filter((row) => !row.skipped), [rows]);
  const skippedRows = useMemo(() => rows.filter((row) => row.skipped), [rows]);
  const failedRows = useMemo(() => activeRows.filter((row) => row.errors.length > 0), [activeRows]);
  const readyRows = useMemo(() => activeRows.filter((row) => row.errors.length === 0), [activeRows]);
  const warningCount = useMemo(
    () => activeRows.reduce((total, row) => total + (row.warnings?.length || 0), 0),
    [activeRows],
  );
  const appointmentGroupCount = useMemo(
    () => groupAppointmentRowsByReference(readyRows).size,
    [readyRows],
  );

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setImportResult(null);
    setImportError(null);
    setLoadingCatalogs(true);

    try {
      const text = await file.text();
      const parsed = parseFreshaAppointmentCsv(text);
      if (parsed.errorKey) {
        setRows([]);
        setHeaders([]);
        setFileName('');
        setCatalogs(null);
        setParseErrorKey(parsed.errorKey);
        return;
      }

      const [customers, services, teamMembers] = await Promise.all([
        StoreCustomerService.getStoreCustomerDropdown(storeId),
        StoreService.getServices(false),
        TeamMemberService.getTeamMembersByStore(storeId),
      ]);

      const nextCatalogs = {
        customers: customers || [],
        services: services || [],
        teamMembers: teamMembers || [],
        language: i18n.language,
      };

      setParseErrorKey(null);
      setFileName(file.name);
      setCatalogs(nextCatalogs);
      setRows(resolveAppointmentRowsWithCatalogs(parsed.rows, nextCatalogs));
      setHeaders(parsed.headers || []);
      setDelimiter(parsed.delimiter || ',');
    } catch {
      setRows([]);
      setHeaders([]);
      setFileName('');
      setCatalogs(null);
      setParseErrorKey('dataImport.appointments.errors.parseFailed');
    } finally {
      setLoadingCatalogs(false);
    }
  };

  const handleSaveFixedRow = (updatedRow) => {
    setRows((current) => current.map((row) => (
      row.rowNumber === updatedRow.rowNumber
        ? (updatedRow.skipped ? updatedRow : resolveAppointmentRowsWithCatalogs([updatedRow], catalogs)[0])
        : row
    )));
  };

  const runImport = async (rowsToImport) => {
    setImporting(true);
    setImportError(null);
    try {
      const payload = buildImportAppointmentsPayload(rowsToImport);
      if (!payload.appointments.length) {
        setImportError(t('dataImport.appointments.errors.noReadyRows'));
        return;
      }
      const result = await StoreDataImportService.importAppointments(storeId, payload);
      setImportResult(result);
      onCompleted?.(result);
    } catch (error) {
      const message = error?.response?.data?.message
        || error?.response?.data?.title
        || t('dataImport.appointments.errors.importFailed');
      setImportError(message);
    } finally {
      setImporting(false);
      setImportReadyConfirmOpen(false);
    }
  };

  const handleImportClick = async () => {
    if (failedRows.length > 0 || readyRows.length === 0 || disabled) return;
    await runImport(readyRows);
  };

  const handleImportReadyConfirm = async () => {
    if (!readyRows.length || disabled) return;
    await runImport(readyRows);
  };

  const handleDownloadFailingCsv = () => {
    if (!failedRows.length) return;

    const csv = buildFailingAppointmentsCsv(failedRows, headers, delimiter);
    const baseName = (fileName || 'fresha-appointments').replace(/\.csv$/i, '');
    downloadTextFile(csv, `${baseName}-failing-rows.csv`);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: `1px solid ${brandAlpha('accent', 0.35)}`,
          background: BRAND.background,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.primaryText, mb: 1 }}>
          {t('dataImport.appointments.helper.title')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {t('dataImport.appointments.helper.intro')}
        </Typography>
        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
          <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
            {t('dataImport.appointments.helper.step1')}
            {' '}
            <Link href={FRESHA_APPOINTMENTS_EXPORT_URL} target="_blank" rel="noopener noreferrer">
              {t('dataImport.appointments.helper.openFresha')}
              <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
            </Link>
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
            {t('dataImport.appointments.helper.step2')}
          </Typography>
          <Typography component="li" variant="body2">
            {t('dataImport.appointments.helper.step3')}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1.5, color: '#666' }}>
          {t('dataImport.appointments.helper.languageNote')}
        </Typography>
      </Box>

      <Box>
        <Button
          component="label"
          variant="outlined"
          startIcon={loadingCatalogs ? <CircularProgress size={18} /> : <CloudUploadIcon />}
          disabled={disabled || importing || loadingCatalogs || Boolean(importResult)}
          sx={uploadButtonSx}
        >
          {loadingCatalogs
            ? t('dataImport.appointments.resolving')
            : t('dataImport.appointments.uploadButton')}
          <input hidden type="file" accept=".csv,text/csv" onChange={handleFileChange} />
        </Button>
        {fileName && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('dataImport.appointments.selectedFile', { fileName })}
          </Typography>
        )}
      </Box>

      {parseErrorKey && <Alert severity="error">{t(parseErrorKey)}</Alert>}
      {importError && <Alert severity="error">{importError}</Alert>}

      {rows.length > 0 && !importResult && (
        <>
          <Alert severity={failedRows.length ? 'warning' : 'success'}>
            {t('dataImport.appointments.summary', {
              total: activeRows.length,
              appointments: appointmentGroupCount,
              ready: readyRows.length,
              failed: failedRows.length,
              warnings: warningCount,
            })}
          </Alert>

          {skippedRows.length > 0 && (
            <Alert severity="info">
              {t('dataImport.appointments.skippedNotice', { count: skippedRows.length })}
            </Alert>
          )}

          {failedRows.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t('dataImport.appointments.failedTitle')}
              </Typography>
              {failedRows.slice(0, 8).map((row) => (
                <Typography key={row.rowNumber} variant="body2">
                  #{row.rowNumber}: {row.errors.map((errorKey) => t(errorKey)).join(', ')}
                </Typography>
              ))}
              {failedRows.length > 8 && (
                <Typography variant="body2" color="text.secondary">
                  {t('dataImport.appointments.moreFailures', { count: failedRows.length - 8 })}
                </Typography>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 2,
                  mt: 0.5,
                }}
              >
                <Button
                  variant="text"
                  startIcon={<HandymanIcon />}
                  onClick={() => setFixOpen(true)}
                  sx={textActionSx}
                >
                  {t('dataImport.appointments.fixFailingRows')}
                </Button>
                <Button
                  variant="text"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadFailingCsv}
                  sx={textActionSx}
                >
                  {t('dataImport.appointments.downloadFailingRows')}
                </Button>
                <PrimaryActionButton
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setImportReadyConfirmOpen(true)}
                  disabled={!readyRows.length || importing}
                >
                  {t('dataImport.appointments.importReadyRows')}
                </PrimaryActionButton>
              </Box>
            </Box>
          )}

          {warningCount > 0 && (
            <Alert severity="info">
              {t('dataImport.appointments.warningsNotice', { count: warningCount })}
            </Alert>
          )}

          {failedRows.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <PrimaryActionButton
                onClick={handleImportClick}
                disabled={disabled || importing || readyRows.length === 0}
              >
                {importing ? t('dataImport.appointments.importing') : t('dataImport.appointments.importButton')}
              </PrimaryActionButton>
            </Box>
          )}
        </>
      )}

      {importResult && (
        <Alert severity="success">
          {t('dataImport.appointments.importSuccess', {
            created: importResult.createdCount,
          })}
        </Alert>
      )}

      <FixFailedImportAppointmentsDialog
        open={fixOpen}
        onClose={() => setFixOpen(false)}
        rows={rows}
        catalogs={catalogs}
        onApply={handleSaveFixedRow}
      />

      <ConfirmDialog
        open={importReadyConfirmOpen}
        onClose={() => setImportReadyConfirmOpen(false)}
        onConfirm={handleImportReadyConfirm}
        title={t('dataImport.appointments.importReadyConfirmTitle')}
        message={t('dataImport.appointments.importReadyConfirmMessage', {
          failed: failedRows.length,
          ready: readyRows.length,
        })}
        confirmText={t('dataImport.appointments.importReadyConfirmAction')}
        confirmColor="primary"
        loading={importing}
      />
    </Box>
  );
};

export default ImportAppointmentsStep;
